(() => {
  const CLOSE_DURATION = 380;

  const initializeMenus = (root = document) => {
    root.querySelectorAll('[data-azul-menu]').forEach((menu) => {
      if (menu.dataset.azulInitialized === 'true') return;
      menu.dataset.azulInitialized = 'true';

      const summary = menu.querySelector(':scope > summary');
      const closeButtons = menu.querySelectorAll('[data-azul-close]');
      const drawer = menu.querySelector('.azul-drawer');
      const drawerLayer = menu.querySelector('[data-azul-drawer-layer]');
      const drawerSearchForm = menu.querySelector('.azul-drawer__search-form');
      const drawerSearchInput = drawerSearchForm?.querySelector('.azul-drawer__search-input');
      let closeTimer;
      let drawerSearchSubmitted = false;
      let drawerSearchWasFocused = false;

      const setMenuState = (isOpen) => {
        document.body.classList.toggle('azul-menu-open', isOpen);
        summary?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (summary) {
          const label = isOpen ? summary.dataset.closeLabel : summary.dataset.openLabel;
          if (label) summary.setAttribute('aria-label', label);
        }
      };

      const closeMenu = () => {
        if (!menu.open || menu.classList.contains('is-closing')) return;

        window.clearTimeout(closeTimer);
        menu.classList.add('is-closing');
        document.body.classList.add('azul-menu-closing');
        summary?.setAttribute('aria-expanded', 'false');
        if (summary?.dataset.openLabel) summary.setAttribute('aria-label', summary.dataset.openLabel);

        closeTimer = window.setTimeout(() => {
          menu.removeAttribute('open');
          menu.classList.remove('is-closing');
          document.body.classList.remove('azul-menu-closing');
          setMenuState(false);
        }, CLOSE_DURATION);
      };

      summary?.addEventListener('click', (event) => {
        if (menu.open) {
          event.preventDefault();
          closeMenu();
        }
      });

      menu.addEventListener('toggle', () => {
        if (menu.open && !menu.classList.contains('is-closing')) {
          setMenuState(true);
        } else if (!menu.open) {
          menu.classList.remove('is-closing');
          document.body.classList.remove('azul-menu-closing');
          setMenuState(false);
        }
      });

      closeButtons.forEach((button) => button.addEventListener('click', closeMenu));

      const restoreDrawerAfterSearch = () => {
        if (!menu.open || drawerSearchSubmitted || !drawerSearchWasFocused) return;

        drawerSearchWasFocused = false;
        drawerSearchInput?.blur();
        if (drawerSearchInput) drawerSearchInput.value = '';

        const resetScroll = () => {
          if (!menu.open) return;
          if (drawer) drawer.scrollTop = 0;
          if (drawerLayer) drawerLayer.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          window.scrollTo(0, 0);
          summary?.setAttribute('aria-expanded', 'true');
          document.body.classList.add('azul-menu-open');
        };

        window.requestAnimationFrame(resetScroll);
        window.setTimeout(resetScroll, 80);
        window.setTimeout(resetScroll, 280);
      };

      if (drawerSearchForm && drawerSearchInput) {
        drawerSearchInput.addEventListener('focus', () => {
          drawerSearchWasFocused = true;
          drawerSearchSubmitted = false;
        });

        drawerSearchForm.addEventListener('submit', () => {
          drawerSearchSubmitted = true;
        });

        drawerSearchInput.addEventListener('blur', () => {
          window.setTimeout(() => {
            if (!drawerSearchForm.contains(document.activeElement)) restoreDrawerAfterSearch();
          }, 0);
        });

        window.visualViewport?.addEventListener('resize', () => {
          if (drawerSearchWasFocused && document.activeElement !== drawerSearchInput) {
            window.setTimeout(restoreDrawerAfterSearch, 40);
          }
        });
      }

      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && document.activeElement === drawerSearchInput) {
          event.preventDefault();
          restoreDrawerAfterSearch();
          return;
        }
        if (event.key === 'Escape') closeMenu();
      });

      document.body.classList.remove('azul-menu-closing');
      setMenuState(menu.open);
    });
  };

  const initializeVideoToggles = (root = document) => {
    root.querySelectorAll('[data-azul-video-toggle]').forEach((button) => {
      if (button.dataset.azulInitialized === 'true') return;
      button.dataset.azulInitialized = 'true';

      const section = button.closest('.azul-hero');
      const video = section?.querySelector('video');
      if (!video) return;

      button.addEventListener('click', () => {
        if (video.paused) {
          video.play().catch(() => {});
          button.classList.remove('is-paused');
          button.setAttribute('aria-label', 'Video pausieren');
        } else {
          video.pause();
          button.classList.add('is-paused');
          button.setAttribute('aria-label', 'Video abspielen');
        }
      });
    });
  };

  const initializeInlineSearch = (root = document) => {
    root.querySelectorAll('[data-azul-inline-search]').forEach((form) => {
      if (form.dataset.azulInitialized === 'true') return;
      form.dataset.azulInitialized = 'true';

      const input = form.querySelector('.azul-header__search-input');
      if (!input) return;

      const activate = () => form.classList.add('is-active');
      const deactivate = () => {
        if (document.activeElement !== input) form.classList.remove('is-active');
      };

      form.addEventListener('click', (event) => {
        if (event.target !== input) input.focus();
        activate();
      });

      input.addEventListener('focus', activate);
      input.addEventListener('blur', () => window.setTimeout(deactivate, 0));

      form.addEventListener('submit', (event) => {
        if (!input.value.trim()) {
          event.preventDefault();
          activate();
          input.focus();
        }
      });
    });
  };

  const initializeLoginDrawer = (root = document) => {
    const layer = document.querySelector('[data-azul-login-layer]');
    if (!layer || layer.dataset.azulInitialized === 'true') return;
    layer.dataset.azulInitialized = 'true';

    const panel = layer.querySelector('.azul-login-panel');
    const openButtons = document.querySelectorAll('[data-azul-login-open]');
    const closeButtons = layer.querySelectorAll('[data-azul-login-close]');
    const loginView = layer.querySelector('[data-azul-login-view="login"]');
    const recoverView = layer.querySelector('[data-azul-login-view="recover"]');
    const recoverOpen = layer.querySelector('[data-azul-recover-open]');
    const recoverClose = layer.querySelector('[data-azul-recover-close]');
    const loginForm = layer.querySelector('form[action*="/account/login"]');
    const loginEmail = layer.querySelector('input[name="customer[email]"]');
    const rememberCheckbox = layer.querySelector('input[name="remember"]');
    let lastTrigger = null;

    try {
      const rememberedEmail = window.localStorage.getItem('azulRememberedEmail');
      if (rememberedEmail && loginEmail && rememberCheckbox) {
        loginEmail.value = rememberedEmail;
        rememberCheckbox.checked = true;
      }
    } catch (error) {
      // Storage can be unavailable in private browsing contexts.
    }

    const showView = (view) => {
      const showRecover = view === 'recover';
      if (loginView) loginView.hidden = showRecover;
      if (recoverView) recoverView.hidden = !showRecover;
      const focusTarget = layer.querySelector(showRecover ? '#AzulRecoverEmail-' + panel.getAttribute('aria-labelledby').replace('AzulLoginTitle-', '') : 'input[name="customer[email]"]');
      window.setTimeout(() => focusTarget?.focus(), 50);
    };

    const closeOpenMenu = () => {
      document.querySelectorAll('[data-azul-menu][open]').forEach((menu) => {
        menu.removeAttribute('open');
        menu.classList.remove('is-closing');
      });
      document.body.classList.remove('azul-menu-open');
    };

    const openDrawer = (trigger, view = 'login') => {
      lastTrigger = trigger || document.activeElement;
      closeOpenMenu();
      showView(view);
      layer.classList.add('is-open');
      layer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('azul-login-open');
      window.setTimeout(() => {
        const focusTarget = layer.querySelector(view === 'recover' ? 'input[name="email"]' : 'input[name="customer[email]"]');
        if (focusTarget) focusTarget.focus();
        else panel?.focus();
      }, 120);
    };

    const closeDrawer = () => {
      layer.classList.remove('is-open');
      layer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('azul-login-open');
      window.setTimeout(() => lastTrigger?.focus?.(), 120);
    };

    openButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer(button);
      });
    });

    closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));
    recoverOpen?.addEventListener('click', () => showView('recover'));
    recoverClose?.addEventListener('click', () => showView('login'));

    layer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDrawer();
    });

    loginForm?.addEventListener('submit', () => {
      try {
        if (rememberCheckbox?.checked && loginEmail?.value) {
          window.localStorage.setItem('azulRememberedEmail', loginEmail.value);
        } else {
          window.localStorage.removeItem('azulRememberedEmail');
        }
      } catch (error) {
        // Storage can be unavailable in private browsing contexts.
      }
    });

    const loginErrors = layer.querySelector('[data-azul-login-form-errors="true"]');
    const recoverState = layer.querySelector('[data-azul-recover-state]');
    if (loginErrors) openDrawer(null, 'login');
    if (recoverState?.dataset.recoverErrors === 'true' || recoverState?.dataset.recoverSuccess === 'true') {
      openDrawer(null, 'recover');
    }
  };

  const initializeAccountEvents = (root = document) => {
    root.querySelectorAll('.azul-account__order-list').forEach((list) => {
      if (list.dataset.azulSorted === 'true') return;
      list.dataset.azulSorted = 'true';

      const cards = Array.from(list.querySelectorAll('.azul-account__order-card[data-event-sort]'));
      cards
        .sort((a, b) => Number(b.dataset.eventSort || 0) - Number(a.dataset.eventSort || 0))
        .forEach((card) => list.appendChild(card));
    });

    root.querySelectorAll('[data-azul-calendar]').forEach((button) => {
      if (button.dataset.azulInitialized === 'true') return;
      button.dataset.azulInitialized = 'true';

      button.addEventListener('click', () => {
        const title = button.dataset.eventTitle || 'AZULHOUSE Event';
        const dateValue = button.dataset.eventDate;
        const eventUrl = button.dataset.eventUrl || window.location.href;
        if (!dateValue) return;

        const [year, month, day] = dateValue.split('-').map(Number);
        if (!year || !month || !day) return;

        const formatDate = (date) => [
          date.getUTCFullYear(),
          String(date.getUTCMonth() + 1).padStart(2, '0'),
          String(date.getUTCDate()).padStart(2, '0'),
        ].join('');

        const startDate = new Date(Date.UTC(year, month - 1, day));
        const endDate = new Date(Date.UTC(year, month - 1, day + 1));
        const calendarUrl = new URL('https://calendar.google.com/calendar/render');
        calendarUrl.searchParams.set('action', 'TEMPLATE');
        calendarUrl.searchParams.set('text', title);
        calendarUrl.searchParams.set('dates', `${formatDate(startDate)}/${formatDate(endDate)}`);
        calendarUrl.searchParams.set('details', `AZULHOUSE Event. Details: ${eventUrl}`);

        window.open(calendarUrl.toString(), '_blank', 'noopener,noreferrer');
      });
    });
  };

  const initializeAdminDashboard = (root = document) => {
    root.querySelectorAll('[data-azul-admin-dashboard]').forEach((dashboard) => {
      if (dashboard.dataset.azulInitialized === 'true') return;
      dashboard.dataset.azulInitialized = 'true';

      const accountSection = dashboard.closest('.azul-account');
      const memberView = accountSection?.querySelector('[data-azul-member-view]');
      const openButtons = accountSection?.querySelectorAll('[data-azul-admin-open]') || [];
      const closeButtons = dashboard.querySelectorAll('[data-azul-admin-close]');
      const refreshButtons = dashboard.querySelectorAll('[data-azul-admin-refresh]');
      const endpoint = (dashboard.dataset.endpoint || '').trim();
      const refreshSeconds = Math.max(15, Number(dashboard.dataset.refreshSeconds || 60));
      let refreshTimer = null;
      let hasLoaded = false;
      let activeRequest = null;

      const stateElements = {
        loading: dashboard.querySelector('[data-azul-admin-state="loading"]'),
        error: dashboard.querySelector('[data-azul-admin-state="error"]'),
        content: dashboard.querySelector('[data-azul-admin-state="content"]'),
      };

      const setState = (state) => {
        Object.entries(stateElements).forEach(([name, element]) => {
          if (element) element.hidden = name !== state;
        });
      };

      const text = (value, fallback = '—') => {
        if (value === null || value === undefined || value === '') return fallback;
        return String(value);
      };

      const formatDate = (value, includeTime = false) => {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return text(value);
        return new Intl.DateTimeFormat('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
        }).format(date);
      };

      const makeCell = (value) => {
        const cell = document.createElement('td');
        cell.textContent = text(value);
        return cell;
      };

      const appendEmptyRow = (tbody, colspan, message) => {
        const row = document.createElement('tr');
        row.className = 'azul-admin-dashboard__empty-row';
        const cell = document.createElement('td');
        cell.colSpan = colspan;
        cell.textContent = message;
        row.appendChild(cell);
        tbody.appendChild(row);
      };

      const statusClass = (status = '') => {
        const normalized = String(status).toLowerCase();
        if (['checked_in', 'checked-in', 'paid', 'fulfilled', 'success', 'active'].some((key) => normalized.includes(key))) return 'is-success';
        if (['pending', 'open', 'partial', 'warning'].some((key) => normalized.includes(key))) return 'is-warning';
        if (['failed', 'cancelled', 'canceled', 'refunded', 'error'].some((key) => normalized.includes(key))) return 'is-error';
        return '';
      };

      const renderMetrics = (metrics = {}, notes = {}) => {
        dashboard.querySelectorAll('[data-azul-admin-metric]').forEach((element) => {
          const key = element.dataset.azulAdminMetric;
          let value = metrics[key];
          if (key === 'occupancyToday' && typeof value === 'number') value = `${Math.round(value)}%`;
          element.textContent = text(value);
        });
        dashboard.querySelectorAll('[data-azul-admin-metric-note]').forEach((element) => {
          const key = element.dataset.azulAdminMetricNote;
          if (notes[key]) element.textContent = notes[key];
        });
      };

      const setCount = (key, value) => {
        const element = dashboard.querySelector(`[data-azul-admin-count="${key}"]`);
        if (element) element.textContent = String(Array.isArray(value) ? value.length : Number(value || 0));
      };

      const renderCustomers = (customers = []) => {
        const tbody = dashboard.querySelector('[data-azul-admin-recent-customers]');
        if (!tbody) return;
        tbody.replaceChildren();
        setCount('recentCustomers', customers);
        if (!customers.length) {
          appendEmptyRow(tbody, 4, 'Keine neuen Registrierungen im ausgewählten Zeitraum.');
          return;
        }
        customers.forEach((customer) => {
          const row = document.createElement('tr');
          row.append(
            makeCell(customer.name || customer.displayName || 'Unbekannt'),
            makeCell(customer.email || '—'),
            makeCell(formatDate(customer.createdAt || customer.registeredAt, true)),
            makeCell(customer.ordersCount ?? customer.orderCount ?? 0),
          );
          tbody.appendChild(row);
        });
      };

      const renderCheckins = (checkins = []) => {
        const container = dashboard.querySelector('[data-azul-admin-recent-checkins]');
        if (!container) return;
        container.replaceChildren();
        setCount('recentCheckins', checkins);
        if (!checkins.length) {
          const empty = document.createElement('p');
          empty.className = 'azul-admin-dashboard__empty-list';
          empty.textContent = 'Noch keine Check-ins vorhanden.';
          container.appendChild(empty);
          return;
        }
        checkins.forEach((checkin) => {
          const item = document.createElement('article');
          item.className = 'azul-admin-dashboard__activity-item';
          const top = document.createElement('div');
          top.className = 'azul-admin-dashboard__activity-top';
          const name = document.createElement('strong');
          name.textContent = text(checkin.customerName || checkin.name, 'Unbekannter Gast');
          const time = document.createElement('time');
          time.dateTime = checkin.checkedInAt || '';
          time.textContent = formatDate(checkin.checkedInAt, true);
          top.append(name, time);
          const event = document.createElement('p');
          event.textContent = [checkin.eventName, checkin.ticketCode].filter(Boolean).join(' · ') || 'Event nicht angegeben';
          const status = document.createElement('span');
          status.className = `azul-admin-dashboard__status ${statusClass(checkin.status || 'checked_in')}`.trim();
          status.textContent = text(checkin.statusLabel || checkin.status, 'Eingecheckt');
          item.append(top, event, status);
          container.appendChild(item);
        });
      };

      const renderEvents = (events = []) => {
        const container = dashboard.querySelector('[data-azul-admin-events]');
        if (!container) return;
        container.replaceChildren();
        setCount('events', events);
        if (!events.length) {
          const empty = document.createElement('p');
          empty.className = 'azul-admin-dashboard__empty-list';
          empty.textContent = 'Keine kommenden Events gefunden.';
          container.appendChild(empty);
          return;
        }
        events.forEach((event) => {
          const sold = Number(event.sold || event.ticketsSold || 0);
          const capacity = Number(event.capacity || 0);
          const checkedIn = Number(event.checkedIn || event.checkins || 0);
          const percent = capacity > 0 ? Math.min(100, Math.round((sold / capacity) * 100)) : Number(event.occupancy || 0);
          const item = document.createElement('article');
          item.className = 'azul-admin-dashboard__event-item';
          const top = document.createElement('div');
          top.className = 'azul-admin-dashboard__event-top';
          const name = document.createElement('strong');
          name.textContent = text(event.name || event.title, 'AZULHOUSE Event');
          const time = document.createElement('time');
          time.dateTime = event.startsAt || event.date || '';
          time.textContent = formatDate(event.startsAt || event.date, true);
          top.append(name, time);
          const stats = document.createElement('div');
          stats.className = 'azul-admin-dashboard__event-stats';
          [
            `${sold} verkauft`,
            `${checkedIn} eingecheckt`,
            capacity ? `${capacity} Kapazität` : null,
          ].filter(Boolean).forEach((label) => {
            const span = document.createElement('span');
            span.textContent = label;
            stats.appendChild(span);
          });
          const progress = document.createElement('div');
          progress.className = 'azul-admin-dashboard__event-progress';
          const bar = document.createElement('span');
          bar.style.setProperty('--azul-event-progress', `${Math.max(0, Math.min(100, percent))}%`);
          progress.appendChild(bar);
          item.append(top, stats, progress);
          container.appendChild(item);
        });
      };

      const renderOrders = (orders = []) => {
        const tbody = dashboard.querySelector('[data-azul-admin-recent-orders]');
        if (!tbody) return;
        tbody.replaceChildren();
        setCount('recentOrders', orders);
        if (!orders.length) {
          appendEmptyRow(tbody, 5, 'Keine Bestellungen im ausgewählten Zeitraum.');
          return;
        }
        orders.forEach((order) => {
          const row = document.createElement('tr');
          row.append(
            makeCell(order.name || order.orderName || order.id),
            makeCell(order.customerName || order.customer || 'Gast'),
            makeCell(order.eventName || order.event || '—'),
          );
          const statusCell = document.createElement('td');
          const status = document.createElement('span');
          status.className = `azul-admin-dashboard__status ${statusClass(order.status)}`.trim();
          status.textContent = text(order.statusLabel || order.status);
          statusCell.appendChild(status);
          row.append(statusCell, makeCell(order.total || order.totalFormatted || '—'));
          tbody.appendChild(row);
        });
      };

      const renderDashboard = (data = {}) => {
        renderMetrics(data.metrics || data.overview || {}, data.metricNotes || {});
        renderCustomers(data.recentCustomers || data.customers || []);
        renderCheckins(data.recentCheckins || data.checkins || []);
        renderEvents(data.events || data.upcomingEvents || []);
        renderOrders(data.recentOrders || data.orders || []);
        const updated = dashboard.querySelector('[data-azul-admin-updated]');
        if (updated) updated.textContent = formatDate(data.generatedAt || data.updatedAt || new Date().toISOString(), true);
        setState('content');
        hasLoaded = true;
      };

      const showError = (message) => {
        const element = dashboard.querySelector('[data-azul-admin-error-message]');
        if (element) element.textContent = message;
        setState('error');
      };

      const loadDashboard = async () => {
        if (!endpoint) {
          showError('Im Theme-Editor ist noch kein Admin-Dashboard-Endpunkt hinterlegt.');
          return;
        }
        if (activeRequest) activeRequest.abort();
        activeRequest = new AbortController();
        if (!hasLoaded) setState('loading');
        try {
          const url = new URL(endpoint, window.location.origin);
          url.searchParams.set('range', '7d');
          const response = await fetch(url.toString(), {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: activeRequest.signal,
          });
          if (!response.ok) {
            if (response.status === 401 || response.status === 403) throw new Error('Keine Berechtigung für die Admin-Daten.');
            throw new Error(`Dashboard-Endpunkt antwortet mit Status ${response.status}.`);
          }
          const data = await response.json();
          renderDashboard(data);
        } catch (error) {
          if (error.name === 'AbortError') return;
          showError(error.message || 'Die Dashboard-Daten konnten nicht geladen werden.');
        } finally {
          activeRequest = null;
        }
      };

      const stopRefresh = () => {
        if (refreshTimer) window.clearInterval(refreshTimer);
        refreshTimer = null;
      };

      const startRefresh = () => {
        stopRefresh();
        refreshTimer = window.setInterval(() => {
          if (!dashboard.hidden && document.visibilityState === 'visible') loadDashboard();
        }, refreshSeconds * 1000);
      };

      const openDashboard = () => {
        if (memberView) memberView.hidden = true;
        dashboard.hidden = false;
        dashboard.scrollIntoView({ block: 'start' });
        loadDashboard();
        startRefresh();
      };

      const closeDashboard = () => {
        dashboard.hidden = true;
        if (memberView) memberView.hidden = false;
        stopRefresh();
        accountSection?.scrollIntoView({ block: 'start' });
      };

      openButtons.forEach((button) => button.addEventListener('click', openDashboard));
      closeButtons.forEach((button) => button.addEventListener('click', closeDashboard));
      refreshButtons.forEach((button) => button.addEventListener('click', loadDashboard));
      document.addEventListener('visibilitychange', () => {
        if (!dashboard.hidden && document.visibilityState === 'visible') loadDashboard();
      });
    });
  };

  const initialize = (root = document) => {
    initializeMenus(root);
    initializeVideoToggles(root);
    initializeInlineSearch(root);
    initializeLoginDrawer(root);
    initializeAccountEvents(root);
    initializeAdminDashboard(root);
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
